import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { FileText, Check, ShieldCheck, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/format";

/**
 * RgpdContractDialog — displays the full GDPR (RGPD) data-processing contract
 * between QRISE (SARL DEAL RUN) and the current merchant. The merchant's legal
 * name is automatically substituted everywhere "[CLIENT]" appears.
 *
 * Two modes:
 *   - requireAcceptance=true  → mandatory on first login, not dismissible,
 *     must check "J'ai lu et j'accepte" + click "Accepter".
 *   - requireAcceptance=false → read-only view (post-acceptance), shows the
 *     acceptance date/version footer and a single "Fermer" button.
 */
export default function RgpdContractDialog({
  open,
  onOpenChange,
  merchant,
  requireAcceptance = false,
  onAccepted,
}) {
  const { t, i18n } = useTranslation();
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const c = useContractText(open, i18n.language?.startsWith("en") ? "en" : "fr");

  // Resolve the client name shown inside the contract. We prefer the legal
  // entity name, then the trading company, then the display name.
  const clientName =
    merchant?.legal_name ||
    merchant?.company ||
    merchant?.name ||
    c?.default_client ||
    "—";

  async function handleAccept() {
    setSubmitting(true);
    try {
      const { data } = await api.post("/merchant/accept-rgpd");
      toast.success(t("rgpd.accepted_toast"));
      onAccepted?.(data);
      onOpenChange?.(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        // When acceptance is required, block dismissal via ESC / outside click.
        if (requireAcceptance && !v) return;
        onOpenChange?.(v);
      }}
    >
      <DialogContent
        className={`max-w-3xl max-h-[90vh] p-0 flex flex-col gap-0 overflow-hidden ${
          requireAcceptance ? "[&>button]:hidden" : ""
        }`}
        data-testid="rgpd-dialog"
        onInteractOutside={(e) => {
          if (requireAcceptance) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (requireAcceptance) e.preventDefault();
        }}
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border/60">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="h-4 w-4 text-foreground" />
            <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
              {t("rgpd.eyebrow")}
            </span>
          </div>
          <DialogTitle className="text-2xl font-semibold tracking-tight">
            {t("rgpd.title")}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {t("rgpd.subtitle")}
          </DialogDescription>
        </DialogHeader>

        {/* Contract body (scrollable) — text served by the API in the UI language */}
        <div
          className="flex-1 overflow-y-auto px-6 py-5 text-[13px] leading-relaxed text-foreground/90 space-y-4"
          data-testid="rgpd-contract-body"
        >
          {!c ? (
            <div className="flex items-center justify-center py-16" data-testid="rgpd-contract-loading">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <ContractText c={c} clientName={clientName} />
          )}

          {merchant?.rgpd_accepted_at && (
            <div className="mt-4 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3 text-[12px]">
              <div className="flex items-center gap-1.5 font-semibold text-emerald-600 dark:text-emerald-400">
                <Check className="h-3.5 w-3.5" />
                {t("rgpd.already_accepted_title")}
              </div>
              <div className="mt-1 text-muted-foreground">
                {t("rgpd.already_accepted_hint", {
                  date: formatDate(merchant.rgpd_accepted_at),
                  version: merchant.rgpd_accepted_version || "—",
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="border-t border-border/60 px-6 py-4 bg-muted/30">
          {requireAcceptance ? (
            <div className="space-y-3">
              <label
                className="flex items-start gap-2.5 cursor-pointer text-[13px] leading-snug"
                data-testid="rgpd-accept-checkbox-label"
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={(v) => setChecked(Boolean(v))}
                  className="mt-0.5"
                  data-testid="rgpd-accept-checkbox"
                />
                <span>
                  {t("rgpd.checkbox_i_accept", { client: clientName })}
                </span>
              </label>
              <div className="flex justify-end">
                <Button
                  onClick={handleAccept}
                  disabled={!checked || submitting}
                  className="rounded-xl h-10 bg-foreground text-background hover:bg-foreground/90"
                  data-testid="rgpd-accept-btn"
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                  ) : (
                    <ShieldCheck className="h-4 w-4 mr-1.5" />
                  )}
                  {t("rgpd.cta_accept")}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex justify-end">
              <Button
                variant="outline"
                onClick={() => onOpenChange?.(false)}
                className="rounded-xl h-10"
                data-testid="rgpd-close-btn"
              >
                {t("common.close")}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function useContractText(open, lang) {
  const [byLang, setByLang] = useState({});
  useEffect(() => {
    if (!open || byLang[lang]) return;
    api
      .get("/rgpd-contract/text", { params: { lang } })
      .then((r) => setByLang((prev) => ({ ...prev, [lang]: r.data })))
      .catch(() => {});
  }, [open, lang, byLang]);
  return byLang[lang];
}

// "{client}" placeholders → bold merchant legal name.
function WithClient({ text, clientName }) {
  const parts = text.split("{client}");
  return parts.map((p, i) => (
    <React.Fragment key={i}>
      {p}
      {i < parts.length - 1 && <strong className="text-foreground">{clientName}</strong>}
    </React.Fragment>
  ));
}

function ContractParties({ c, clientName }) {
  return (
    <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-2 text-[12.5px]">
      <div>
        <strong>{c.processor_name}</strong>
        <div className="text-muted-foreground">
          {c.processor_lines.map((l) => (
            <div key={l}>{l}</div>
          ))}
        </div>
        <div className="mt-1 italic text-muted-foreground">{c.processor_designated}</div>
      </div>
      <div className="border-t border-border/60 pt-2">
        <strong>{c.and}</strong>
      </div>
      <div>
        <strong className="text-foreground">{clientName}</strong>
        <div className="mt-1 italic text-muted-foreground">{c.client_designated}</div>
      </div>
    </div>
  );
}

function ContractText({ c, clientName }) {
  return (
    <>
      <section className="space-y-1">
        <div className="text-xs font-semibold text-muted-foreground">{c.site}</div>
        <div className="text-sm font-semibold">{c.platform}</div>
        <div className="text-[12px] text-muted-foreground">
          {c.address}
          <br />
          {c.registry}
        </div>
      </section>
      <div className="text-center py-2">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-border bg-muted/40 text-[11px] font-semibold uppercase tracking-[0.18em]">
          <FileText className="h-3 w-3" />
          {c.badge.replace("{year}", new Date().getFullYear())}
        </div>
      </div>
      <p className="italic text-muted-foreground" data-testid="rgpd-contract-intro">
        <WithClient text={c.intro} clientName={clientName} />
      </p>
      <p>{c.established}</p>
      <ContractParties c={c} clientName={clientName} />
      <p className="text-muted-foreground italic">{c.parties_note}</p>
      {c.sections.map((sec) => (
        <ContractSection key={sec.title} title={sec.title}>
          {sec.intro && <p>{sec.intro}</p>}
          {sec.items.length > 0 && (
            <ol className="list-decimal list-outside ml-5 space-y-1">
              {sec.items.map((it) => (
                <li key={it}>
                  <WithClient text={it} clientName={clientName} />
                </li>
              ))}
            </ol>
          )}
        </ContractSection>
      ))}
      <p className="italic text-muted-foreground pt-2 border-t border-border/50">{c.closing}</p>
    </>
  );
}

function ContractSection({ title, children }) {
  return (
    <section className="space-y-1.5">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      <div className="text-foreground/85">{children}</div>
    </section>
  );
}
